/**
 * Review dialog for PDF/text-extracted BOM lines before applying to site plan.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import type { SCMCategory } from '../../../types/supplyChain';
import { SCM_CATEGORIES, SCM_CATEGORY_LABELS } from '../../../types/supplyChain';
import type { ExtractedBomLine } from '../../../services/supplyChain/extractBomFromPdf';
import { cn } from '../../../lib/utils';

export interface BomUploadReviewDialogProps {
  open: boolean;
  lines: ExtractedBomLine[];
  warnings: readonly string[];
  busy?: boolean;
  mode?: 'create' | 'merge';
  onModeChange?: (mode: 'create' | 'merge') => void;
  onConfirm: (lines: ExtractedBomLine[]) => void;
  onCancel: () => void;
}

export const BomUploadReviewDialog: React.FC<BomUploadReviewDialogProps> = ({
  open,
  lines: initialLines,
  warnings,
  busy = false,
  mode = 'create',
  onModeChange,
  onConfirm,
  onCancel,
}) => {
  const [rows, setRows] = useState<ExtractedBomLine[]>(initialLines);

  useEffect(() => {
    if (open) setRows(initialLines);
  }, [open, initialLines]);

  if (!open) return null;

  const updateRow = (idx: number, patch: Partial<ExtractedBomLine>) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx: number) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="bom-upload-review-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bom-upload-review-title"
    >
      <div className="bg-background border border-border w-full max-w-3xl max-h-[85vh] flex flex-col shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <h3 id="bom-upload-review-title" className="text-sm font-semibold text-foreground">
              Review extracted BoM
            </h3>
            <p className="text-[10px] text-text-secondary mt-0.5">
              Confirm line items before applying — nothing is saved until you click Apply.
            </p>
          </div>
          <button type="button" onClick={onCancel} className="text-text-secondary hover:text-foreground" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {warnings.length > 0 && (
          <div className="px-4 py-2 bg-warning/10 border-b border-warning/30 text-[10px] text-warning flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <ul className="list-disc pl-4 space-y-0.5">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {onModeChange && (
          <div className="px-4 py-2 border-b border-border flex items-center gap-3 text-[10px]">
            <span className="text-text-secondary uppercase tracking-widest">Apply mode</span>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={mode === 'create'} onChange={() => onModeChange('create')} />
              Replace / create plan
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={mode === 'merge'} onChange={() => onModeChange('merge')} />
              Merge into existing
            </label>
          </div>
        )}

        <div className="overflow-auto flex-1 p-4">
          {rows.length === 0 ? (
            <p className="text-[10px] text-text-secondary text-center py-8">No line items to apply.</p>
          ) : (
            <table className="w-full text-[10px]" data-testid="bom-upload-review-table">
              <thead>
                <tr className="text-left text-text-secondary uppercase tracking-widest border-b border-border">
                  <th className="pb-1 pr-2">Name</th>
                  <th className="pb-1 pr-2">Category</th>
                  <th className="pb-1 pr-2 w-16">Qty</th>
                  <th className="pb-1 pr-2 w-24">Unit $</th>
                  <th className="pb-1 w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="border-b border-border/50">
                    <td className="py-1 pr-2">
                      <input
                        value={row.name}
                        onChange={(e) => updateRow(idx, { name: e.target.value })}
                        className="w-full px-1 py-0.5 border border-border bg-background"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <select
                        value={row.category ?? 'power-conversion'}
                        onChange={(e) => updateRow(idx, { category: e.target.value as SCMCategory })}
                        className="w-full px-1 py-0.5 border border-border bg-background"
                      >
                        {SCM_CATEGORIES.map(c => (
                          <option key={c} value={c}>{SCM_CATEGORY_LABELS[c]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1 pr-2">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={row.quantity ?? 1}
                        onChange={(e) => updateRow(idx, { quantity: parseFloat(e.target.value) || 1 })}
                        className="w-full px-1 py-0.5 border border-border bg-background"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={row.unitCostUsd ?? ''}
                        onChange={(e) => updateRow(idx, {
                          unitCostUsd: e.target.value === '' ? null : parseFloat(e.target.value),
                        })}
                        className="w-full px-1 py-0.5 border border-border bg-background"
                        placeholder="—"
                      />
                    </td>
                    <td className="py-1">
                      <button type="button" onClick={() => removeRow(idx)} className="text-text-secondary hover:text-danger" aria-label="Remove row">
                        <X className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 type-body uppercase tracking-widest border border-border"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(rows.filter(r => r.name.trim().length > 0))}
            disabled={busy || rows.length === 0}
            className={cn(
              'px-3 py-1.5 type-body uppercase tracking-widest',
              'bg-primary text-primary-foreground disabled:opacity-50',
            )}
            data-testid="bom-upload-review-confirm"
          >
            {busy ? 'Applying…' : `Apply ${rows.length} items`}
          </button>
        </div>
      </div>
    </div>
  );
};
